import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useState } from "react";
import {
  ClipboardDocumentListIcon,
  ClipboardIcon,
  PlusCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import WorkoutNameEditor from "../WorkoutNameEditor/WorkoutNameEditor";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import FullWidthAsyncButton from "../FullWidthButton/FullWidthAsyncButton";
import axiosClient from "../../axiosClient";
import { useNavigate } from "react-router-dom";
import SuggestionsInput from "../SuggestionsInput/SuggestionsInput";
import FullWidthButton from "../FullWidthButton/FullWidthButton";
import classNames from "classnames";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { format } from "date-fns";

type WorkoutLogType = "create-from-scratch" | "create-from-program";

type CurrentStep = "select-type-step" | "create-step";

interface CreateWorkoutLogModalProps {
  isOpen: boolean;
  onModalClose: () => void;
  previousWorkouts: string[];
  programs: Program[];
}

const CreateWorkoutLogModal: React.FC<CreateWorkoutLogModalProps> = ({
  isOpen,
  onModalClose,
  previousWorkouts,
  programs,
}) => {
  const [typeOfWorkoutLog, setTypeOfWorkoutLog] = useState<WorkoutLogType>(
    "create-from-scratch"
  );
  const [isClosing, setIsClosing] = useState(false);
  const [currentStep, setCurrentStep] =
    useState<CurrentStep>("select-type-step");

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (isClosing) {
      timeoutId = setTimeout(() => {
        setIsClosing(false);
        setCurrentStep("select-type-step");
        setTypeOfWorkoutLog("create-from-scratch");
      }, 300);
    }

    return () => clearTimeout(timeoutId); // Clean up timeout if component unmounts
  }, [isClosing]);

  const handleClose = () => {
    onModalClose();
    setIsClosing(true);
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-4"
            >
              <Dialog.Panel className="relative transform overflow-visible rounded-lg bg-white text-left shadow-xl transition-all mx-4 mb-24 w-full">
                <div className="absolute top-0 right-0 pt-4 pr-4">
                  <XMarkIcon
                    className="h-5 w-auto text-gray-400"
                    aria-hidden="true"
                    onClick={handleClose}
                  />
                </div>
                {currentStep === "select-type-step" && (
                  <TypeOfWorkoutLogScreen
                    selectedOption={typeOfWorkoutLog}
                    setSelectedOption={setTypeOfWorkoutLog}
                    setCurrentStep={setCurrentStep}
                  />
                )}
                {currentStep === "create-step" &&
                  typeOfWorkoutLog === "create-from-scratch" && (
                    <CreateFromScratchScreen
                      previousWorkouts={previousWorkouts}
                      setCurrentStep={setCurrentStep}
                    />
                  )}
                {currentStep === "create-step" &&
                  typeOfWorkoutLog === "create-from-program" && (
                    <CreateFromProgramScreen
                      previousWorkouts={previousWorkouts}
                      programs={programs}
                      setCurrentStep={setCurrentStep}
                    />
                  )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

interface TypeOfWorkoutLogScreenProps {
  selectedOption: WorkoutLogType;
  setSelectedOption: React.Dispatch<React.SetStateAction<WorkoutLogType>>;
  setCurrentStep: React.Dispatch<React.SetStateAction<CurrentStep>>;
}

const TypeOfWorkoutLogScreen: React.FC<TypeOfWorkoutLogScreenProps> = ({
  selectedOption,
  setSelectedOption,
  setCurrentStep,
}) => {
  return (
    <>
      <div className="bg-white px-4 pt-5 pb-4 rounded-lg">
        <ModalHeader
          title="Create a Workout Log"
          paragraph="How would you like to start?"
        />
        <div className="flex flex-col gap-y-4 items-center">
          <CreateWorkoutRadioOption
            value="create-from-scratch"
            title="Start Fresh"
            paragraph="Create a new workout log from scratch. Perfect for when you want complete control over your workout."
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
          />
          <CreateWorkoutRadioOption
            value="create-from-program"
            title="Use a Program"
            paragraph="Pre-populate your workout log with exercises from an existing program. Ideal for when you want a quick setup based on a structured plan."
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
          />
        </div>
      </div>
      <div className="bg-gray-50 rounded-lg px-4 py-3">
        <div className="mb-3 w-full flex justify-center gap-x-2">
          <div className="w-[7px] h-[7px] bg-indigo-600 rounded-full"></div>
          <div className="w-[7px] h-[7px] bg-gray-400/40  rounded-full"></div>
          {selectedOption === "create-from-program" && (
            <div className="w-[7px] h-[7px] bg-gray-400/40  rounded-full"></div>
          )}
        </div>
        <FullWidthButton
          className=""
          text="Next"
          onClick={() => {
            setCurrentStep("create-step");
          }}
        />
      </div>
    </>
  );
};

interface CreateWorkoutRadioOptionProps {
  value: string;
  title: string;
  paragraph: string;
  selectedOption: WorkoutLogType;
  setSelectedOption: React.Dispatch<React.SetStateAction<WorkoutLogType>>;
}

const CreateWorkoutRadioOption: React.FC<CreateWorkoutRadioOptionProps> = ({
  value,
  title,
  paragraph,
  selectedOption,
  setSelectedOption,
}) => {
  return (
    <div
      className={classNames(
        "relative flex flex-row gap-x-4 items-center p-2 border border-gray-300 rounded-lg",
        {
          "border-indigo-600": selectedOption === value,
        }
      )}
      key={value}
      onClick={() => setSelectedOption(value as WorkoutLogType)}
    >
      <input
        id={value}
        value={value}
        name="create-workout-log"
        type="radio"
        className="absolute top-0 right-0 m-2 h-4 w-4 checked:bg-indigo-600 bg-indigo-600 text-indigo-600 focus:ring-indigo-600 hidden"
        tabIndex={-1}
        checked={selectedOption === value}
        onChange={() => {
          setSelectedOption(value as WorkoutLogType);
        }}
      />
      {selectedOption === value && (
        <CheckCircleIcon className="absolute top-0 right-0 m-2 h-auto w-5 rounded-full text-indigo-600" />
      )}
      <div className="">
        {value === "create-from-scratch" && (
          <div className="inline-flex p-2 items-center justify-center rounded-xl bg-indigo-100">
            <ClipboardIcon
              className="h-auto w-[24px] text-indigo-600"
              aria-hidden="true"
            />
          </div>
        )}
        {value === "create-from-program" && (
          <div className="inline-flex p-2 items-center justify-center rounded-xl bg-indigo-100">
            <ClipboardDocumentListIcon
              className="h-auto w-[24px] text-indigo-600"
              aria-hidden="true"
            />
          </div>
        )}
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium leading-6 text-gray-900">
          {title}
        </label>
        <p className="text-xs text-gray-500 whitespace-pre-wrap">{paragraph}</p>
      </div>
    </div>
  );
};

interface CreateFromScratchScreenProps {
  previousWorkouts: string[];
  setCurrentStep: React.Dispatch<React.SetStateAction<CurrentStep>>;
}

const CreateFromScratchScreen: React.FC<CreateFromScratchScreenProps> = ({
  previousWorkouts,
  setCurrentStep,
}) => {
  const navigate = useNavigate();
  const [workoutName, setWorkoutName] = useState<string>("");
  const [workoutDate, setWorkoutDate] = useState<Date>(new Date());

  const handleWorkoutNameChange = (workoutName: string) => {
    setWorkoutName(workoutName);
  };

  const handleCreateWorkoutLog = async () => {
    const newWorkoutLog = {
      title: workoutName.trim(),
      date: workoutDate.toISOString(),
      notes: "",
    };

    try {
      const createWorkoutLogResponse = await axiosClient.post(
        `/api/workouts`,
        newWorkoutLog
      );

      if (createWorkoutLogResponse.status !== 200) {
        throw new Error(createWorkoutLogResponse.data.message);
      }

      const createdWorkoutLog: Workout = createWorkoutLogResponse.data;

      navigate(`/workout/${createdWorkoutLog.id}`, {
        state: {
          workoutTitle: createdWorkoutLog.title,
          workoutDate: createdWorkoutLog.date,
        },
      });
    } catch (e: any) {
      console.error(e);
      const errorMessage =
        e?.response?.data?.error || "Something unexpected occured.";
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg px-4 pt-5 pb-3">
        <ModalHeader
          title="Start Your Fresh Workout Log"
          paragraph=" Begin by giving it a name and choosing a date."
        />
        <div className="flex flex-col gap-y-4">
          <WorkoutNameEditor
            workoutName={workoutName}
            previousWorkouts={previousWorkouts}
            onWorkoutNameChange={handleWorkoutNameChange}
          />
          <div>
            <label
              htmlFor="workout-date"
              className="block mb-1 text-sm font-medium text-gray-700"
            >
              Workout Date
            </label>
            <DatePicker
              name="workout-date"
              tabIndex={-1}
              autoComplete="off"
              selected={workoutDate}
              onChange={(date: Date) => setWorkoutDate(date)}
              popperPlacement="bottom"
              onFocus={(e: { target: { readOnly: boolean } }) =>
                (e.target.readOnly = true)
              }
              onKeyDown={(e: { preventDefault: () => void }) => {
                e.preventDefault();
              }}
            />
          </div>
        </div>
      </div>
      <div className="bg-gray-50 rounded-lg px-4 py-3">
        <div className="mb-3 w-full flex justify-center gap-x-2">
          <div className="w-[7px] h-[7px] bg-gray-400/40  rounded-full"></div>
          <div className="w-[7px] h-[7px] bg-indigo-600 rounded-full"></div>
        </div>
        <FullWidthAsyncButton
          className="mb-3"
          text="Create Workout Log"
          loadingText="Creating Workout Log"
          onClick={handleCreateWorkoutLog}
        />
        <button
          type="button"
          className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 font-medium text-gray-700 shadow-sm text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          onClick={() => {
            setCurrentStep("select-type-step");
          }}
        >
          Back
        </button>
      </div>
    </>
  );
};

type CreateFromProgramStep = "program-step" | "workout-log-step";

interface CreateFromProgramScreenProps {
  previousWorkouts: string[];
  programs: Program[];
  setCurrentStep: React.Dispatch<React.SetStateAction<CurrentStep>>;
}

const CreateFromProgramScreen: React.FC<CreateFromProgramScreenProps> = ({
  previousWorkouts,
  programs,
  setCurrentStep,
}) => {
  const navigate = useNavigate();
  const [workoutName, setWorkoutName] = useState<string>("");
  const [workoutDate, setWorkoutDate] = useState<Date>(new Date());

  const [currentCreateFromProgramStep, setCurrentCreateFromProgramStep] =
    useState<CreateFromProgramStep>("program-step");

  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [programWorkouts, setProgramWorkouts] = useState<WorkoutTemplate[]>([]);
  const [selectedProgramWorkout, setSelectedProgramWorkout] =
    useState<WorkoutTemplate | null>(null);

  useEffect(() => {
    setProgramWorkouts(selectedProgram ? selectedProgram.workoutTemplates : []);

    if (!selectedProgram) {
      setSelectedProgramWorkout(null);
    }
  }, [selectedProgram]);

  useEffect(() => {
    if (selectedProgramWorkout) {
      setWorkoutName(selectedProgramWorkout.name);
    }
  }, [selectedProgramWorkout]);

  const handleWorkoutNameChange = (workoutName: string) => {
    setWorkoutName(workoutName);
  };

  const handleCreateWorkoutLog = async () => {
    const newWorkoutLog = {
      title: workoutName.trim(),
      date: workoutDate.toISOString(),
      WorkoutTemplate: selectedProgramWorkout?._id,
      notes: selectedProgramWorkout?.notes,
    };

    try {
      const createWorkoutLogResponse = await axiosClient.post(
        `/api/workouts`,
        newWorkoutLog
      );

      if (createWorkoutLogResponse.status !== 200) {
        throw new Error(createWorkoutLogResponse.data.message);
      }

      const createdWorkoutLog: Workout = createWorkoutLogResponse.data;

      if (selectedProgramWorkout?.exercises) {
        for (let exerciseTemplate of selectedProgramWorkout.exercises) {
          const newExercise = {
            workoutId: createdWorkoutLog.id,
            date: createdWorkoutLog.date,
            workoutTemplate: selectedProgramWorkout._id,
            exerciseTemplate: exerciseTemplate._id,
            name: exerciseTemplate.name,
            sets: [],
            notes: exerciseTemplate.notes,
          };

          await axiosClient.post(`/api/exercises/`, newExercise);
        }
      }

      navigate(`/workout/${createdWorkoutLog.id}`, {
        state: {
          workoutTitle: createdWorkoutLog.title,
          workoutDate: createdWorkoutLog.date,
        },
      });
    } catch (e: any) {
      console.error(e);
      const errorMessage =
        e?.response?.data?.error || "Something unexpected occured.";
    }
  };

  const handleProgramSelected = (programId: string) => {
    setSelectedProgram(
      programs.find((program) => program._id === programId) || null
    );
  };

  const handleProgramWorkoutSelected = (workoutTemplateId: string) => {
    setSelectedProgramWorkout(
      selectedProgram?.workoutTemplates.find(
        (workoutTemplate) => workoutTemplate._id === workoutTemplateId
      ) || null
    );
  };

  const handleGoToWorkoutLogStep = () => {
    setCurrentCreateFromProgramStep("workout-log-step");
  };

  return (
    <>
      {currentCreateFromProgramStep === "program-step" && (
        <ProgramStep
          programs={programs}
          handleProgramSelected={handleProgramSelected}
          selectedProgram={selectedProgram}
          programWorkouts={programWorkouts}
          handleProgramWorkoutSelected={handleProgramWorkoutSelected}
          selectedProgramWorkout={selectedProgramWorkout}
          handleGoToWorkoutLogStep={handleGoToWorkoutLogStep}
          setCurrentStep={setCurrentStep}
        />
      )}
      {currentCreateFromProgramStep === "workout-log-step" && (
        <WorkoutLogStep
          workoutName={workoutName}
          previousWorkouts={previousWorkouts}
          handleWorkoutNameChange={handleWorkoutNameChange}
          workoutDate={workoutDate}
          setWorkoutDate={setWorkoutDate}
          handleCreateWorkoutLog={handleCreateWorkoutLog}
          setCurrentCreateFromProgramStep={setCurrentCreateFromProgramStep}
        />
      )}
    </>
  );
};

interface ProgramStepProps {
  programs: Program[];
  handleProgramSelected: (programId: string) => void;
  selectedProgram: Program | null;
  programWorkouts: WorkoutTemplate[];
  handleProgramWorkoutSelected: (workoutTemplateId: string) => void;
  selectedProgramWorkout: WorkoutTemplate | null;
  handleGoToWorkoutLogStep: () => void;
  setCurrentStep: React.Dispatch<React.SetStateAction<CurrentStep>>;
}

const ProgramStep: React.FC<ProgramStepProps> = ({
  programs,
  handleProgramSelected,
  selectedProgram,
  programWorkouts,
  handleProgramWorkoutSelected,
  selectedProgramWorkout,
  handleGoToWorkoutLogStep,
  setCurrentStep,
}) => {
  //console.log(programs);
  return (
    <>
      <div className="bg-white rounded-lg px-4 pt-5 pb-3">
        <ModalHeader
          title="Build Your Workout Log from a Program"
          paragraph="Select a program workout and we'll populate the workout log with your plan."
        />
        <SuggestionsInput
          id="program"
          label="Program"
          placeholder="Search for a program"
          suggestions={programs}
          selectedValue={
            selectedProgram && {
              _id: selectedProgram._id,
              name: selectedProgram.title,
            }
          }
          onSelect={handleProgramSelected}
          suggestionLabelEntityProperty="title"
          entityName="Program"
          createEntityLink="/programs"
        />
        <div className="mb-4"></div>
        <SuggestionsInput
          id="program-workout"
          label="Program Workout"
          placeholder="Search for a workout"
          suggestions={programWorkouts}
          selectedValue={
            selectedProgramWorkout && {
              _id: selectedProgramWorkout._id,
              name: selectedProgramWorkout.name,
            }
          }
          onSelect={handleProgramWorkoutSelected}
          suggestionLabelEntityProperty="name"
          disabled={!selectedProgram}
          entityName="Program Workout"
          customEmptyStateMessage="This program has no workouts."
          customNoMatchingSearchesMessage="did not match any of this program's workouts"
          customCreateEntityLinkMessage="Create a new workout for this program"
          createEntityLink={`/programs/${selectedProgram?._id}`}
        />
      </div>
      <div className="bg-gray-50 rounded-lg px-4 py-3">
        <div className="mb-3 w-full flex justify-center gap-x-2">
          <div className="w-[7px] h-[7px] bg-gray-400/40  rounded-full"></div>
          <div className="w-[7px] h-[7px] bg-indigo-600 rounded-full"></div>
          <div className="w-[7px] h-[7px] bg-gray-400/40  rounded-full"></div>
        </div>
        <FullWidthButton
          className="mb-3"
          text="Next"
          onClick={handleGoToWorkoutLogStep}
          disabled={!selectedProgramWorkout}
        />
        <button
          type="button"
          className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 font-medium text-gray-700 shadow-sm text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          onClick={() => {
            setCurrentStep("select-type-step");
          }}
        >
          Back
        </button>
      </div>
    </>
  );
};

interface WorkoutLogStepProps {
  workoutName: string;
  previousWorkouts: string[];
  handleWorkoutNameChange: (workoutName: string) => void;
  workoutDate: Date;
  setWorkoutDate: React.Dispatch<React.SetStateAction<Date>>;
  handleCreateWorkoutLog: () => Promise<void>;
  setCurrentCreateFromProgramStep: React.Dispatch<
    React.SetStateAction<CreateFromProgramStep>
  >;
}

const WorkoutLogStep: React.FC<WorkoutLogStepProps> = ({
  workoutName,
  previousWorkouts,
  handleWorkoutNameChange,
  workoutDate,
  setWorkoutDate,
  handleCreateWorkoutLog,
  setCurrentCreateFromProgramStep,
}) => {
  return (
    <>
      <div className="bg-white rounded-lg px-4 pt-5 pb-3">
        <ModalHeader
          title="Build Your Workout Log from a Program"
          paragraph="Finally, give your workout log a name and select a date."
        />
        <div className="flex flex-col gap-y-4">
          <WorkoutNameEditor
            workoutName={workoutName}
            previousWorkouts={previousWorkouts}
            onWorkoutNameChange={handleWorkoutNameChange}
          />
          <div>
            <label
              htmlFor="workout-date"
              className="block mb-1 text-sm font-medium text-gray-700"
            >
              Workout Date
            </label>
            <DatePicker
              name="workout-date"
              tabIndex={-1}
              autoComplete="off"
              selected={workoutDate}
              onChange={(date: Date) => setWorkoutDate(date)}
              popperPlacement="bottom"
              onFocus={(e: { target: { readOnly: boolean } }) =>
                (e.target.readOnly = true)
              }
              onKeyDown={(e: { preventDefault: () => void }) => {
                e.preventDefault();
              }}
            />
          </div>
        </div>
      </div>
      <div className="bg-gray-50 rounded-lg px-4 py-3">
        <div className="mb-3 w-full flex justify-center gap-x-2">
          <div className="w-[7px] h-[7px] bg-gray-400/40  rounded-full"></div>
          <div className="w-[7px] h-[7px] bg-gray-400/40  rounded-full"></div>
          <div className="w-[7px] h-[7px] bg-indigo-600 rounded-full"></div>
        </div>
        <FullWidthAsyncButton
          className="mb-3"
          text="Create Workout Log"
          loadingText="Creating Workout Log"
          onClick={handleCreateWorkoutLog}
        />
        <button
          type="button"
          className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 font-medium text-gray-700 shadow-sm text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          onClick={() => {
            setCurrentCreateFromProgramStep("program-step");
          }}
        >
          Back
        </button>
      </div>
    </>
  );
};

interface ModalHeaderProps {
  title: string;
  paragraph?: string;
}

const ModalHeader: React.FC<ModalHeaderProps> = ({ title, paragraph }) => {
  return (
    <div className="mb-4">
      <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100">
        <PlusCircleIcon
          className="h-6 w-6 text-indigo-600"
          aria-hidden="true"
        />
      </div>
      <Dialog.Title
        as="h3"
        className="mt-3 text-xl font-medium leading-6 text-gray-900 w-full text-center"
      >
        {title}
      </Dialog.Title>
      {paragraph && (
        <p className="mt-2 text-center text-sm text-gray-500 whitespace-pre-wrap">
          {paragraph}
        </p>
      )}
    </div>
  );
};

export default CreateWorkoutLogModal;
